/**
 * Canonical employment field values — single source of truth.
 * `.value` is the lowercase slug used for comparison (plan/eligibility-group
 * `employmentTypes`, eligibility rules). `.label` is the display form.
 *
 * `employees.employment_type` is STORED as the label ("Full-Time"), never the
 * slug — see `canonicalizeEmploymentType`, which the storage layer applies on
 * every write so "full-time" / "Full time" / "FULL-TIME" collapse into one.
 */

export const EMPLOYMENT_TYPES = [
  { value: "full-time", label: "Full-Time" },
  { value: "part-time", label: "Part-Time" },
  { value: "variable-hour", label: "Variable Hour" },
  { value: "seasonal", label: "Seasonal" },
  { value: "contractor", label: "Contractor" },
  { value: "intern", label: "Intern" },
  { value: "temporary", label: "Temporary" },
] as const;

export const PAY_CYCLES = [
  { value: "weekly", label: "Weekly" },
  { value: "bi-weekly", label: "Bi-Weekly" },
  { value: "semi-monthly", label: "Semi-Monthly" },
  { value: "monthly", label: "Monthly" },
] as const;

export const COMPENSATION_TYPES = [
  { value: "salary", label: "Salary" },
  { value: "hourly", label: "Hourly" },
  { value: "commission", label: "Commission" },
] as const;

export const EMPLOYEE_STATUSES = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "terminated", label: "Terminated" },
  { value: "cobra", label: "COBRA" },
  { value: "leave-of-absence", label: "Leave of Absence" },
  { value: "retired", label: "Retired" },
] as const;

/** Normalize a stored value to its canonical lowercase hyphenated form */
export function normalizeEmploymentValue(raw: string | null | undefined): string {
  return (raw || "").toLowerCase().trim().replace(/\s+/g, "-");
}

/** Input spellings that don't hyphenate cleanly onto a canonical slug. */
const EMPLOYMENT_TYPE_ALIASES: Record<string, (typeof EMPLOYMENT_TYPES)[number]["value"]> = {
  fulltime: "full-time",
  ft: "full-time",
  parttime: "part-time",
  pt: "part-time",
  variable: "variable-hour",
  variablehour: "variable-hour",
  temp: "temporary",
  contract: "contractor",
  "contract-based": "contractor",
};

/**
 * Canonical STORED form of an employment type: the label from
 * EMPLOYMENT_TYPES ("Full-Time") for anything we recognise, Title Case for
 * anything we don't ("seasonal worker" → "Seasonal Worker"), null when empty.
 * Apply on every write to employees.employment_type.
 */
export function canonicalizeEmploymentType(raw: string | null | undefined): string | null {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return null;
  const slug = normalizeEmploymentValue(trimmed).replace(/_/g, "-");
  const resolved = EMPLOYMENT_TYPE_ALIASES[slug] ?? slug;
  const known = EMPLOYMENT_TYPES.find((t) => t.value === resolved);
  if (known) return known.label;
  return trimmed
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/(^|[\s\-/(])([a-z])/g, (_, p, c: string) => p + c.toUpperCase());
}

/** Get display label for a value from any of the above arrays */
export function getLabel(
  options: ReadonlyArray<{ value: string; label: string }>,
  raw: string | null | undefined
): string {
  const normalized = normalizeEmploymentValue(raw);
  return options.find((o) => o.value === normalized)?.label || raw || "";
}
